import React from 'react';
import EmailForm from '../../../components/EmailForm';
import WeddingPhotoGallery from '../../../components/WeddingPhotoGallery';
import CountdownDisplay from '../../../components/CountdownDisplay';
import BestMenAndMaids from '../../../components/BestMenAndMaids';
import { cx } from '../../../shared/styles/runtimeCss';
import {
  FALLBACK_THEME,
  buildSectionShellStyles,
  formatWeddingDate,
  getFieldLabel,
  hasCaptionsEnabled,
  pickResponsiveValue,
  renderLinesWithBreaks,
  splitLines,
} from './rendererShared';

const DESIGN_SYSTEM_BUTTON_COLOR = 'var(--color-primary, #4e7c7f)';

function buildHeroContentStyles(viewportWidth) {
  return {
    position: 'relative',
    zIndex: 3,
    textAlign: 'center',
    padding: pickResponsiveValue(viewportWidth, '1rem', '1.5rem', '2rem'),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  };
}

function buildHeroTextClasses({ section, theme, css, viewportWidth }) {
  const hasImage = Boolean(section?.props?.image);
  const textColor = hasImage ? '#ffffff' : (theme?.colors?.text || FALLBACK_THEME.colors.text);

  return {
    titleClass: css({
      fontSize: pickResponsiveValue(viewportWidth, '1.8rem', '3rem', '5rem'),
      marginBottom: pickResponsiveValue(viewportWidth, '0.6rem', '1rem', '1.5rem'),
      fontFamily: theme?.fonts?.heading || FALLBACK_THEME.fonts.heading,
      textShadow: hasImage ? '2px 2px 4px rgba(0,0,0,0.5)' : undefined,
      fontWeight: 300,
      letterSpacing: pickResponsiveValue(viewportWidth, '0.5px', '1px', '2px'),
      lineHeight: pickResponsiveValue(viewportWidth, '1.1', '1.15', '1.2'),
      color: textColor,
    }),
    subtitleClass: css({
      fontSize: pickResponsiveValue(viewportWidth, '1rem', '1.5rem', '2rem'),
      marginBottom: 0,
      textShadow: hasImage ? '1px 1px 2px rgba(0,0,0,0.5)' : undefined,
      fontWeight: 300,
      letterSpacing: pickResponsiveValue(viewportWidth, '0.3px', '0.5px', '1px'),
      maxWidth: '800px',
      padding: pickResponsiveValue(viewportWidth, '0 0.5rem', '0 1rem', '0'),
      color: textColor,
    }),
    buttonClass: css({
      padding: pickResponsiveValue(viewportWidth, '0.7rem 1.2rem', '0.8rem 1.5rem', '1rem 2rem'),
      fontSize: pickResponsiveValue(viewportWidth, '0.9rem', '1rem', '1.2rem'),
      backgroundColor: DESIGN_SYSTEM_BUTTON_COLOR,
      color: '#ffffff',
      border: 'none',
      borderRadius: theme?.borderRadius?.medium || FALLBACK_THEME.borderRadius.medium,
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: theme?.shadows?.medium || FALLBACK_THEME.shadows.medium,
      marginTop: pickResponsiveValue(viewportWidth, '0.8rem', '1.2rem', '2rem'),
      display: 'inline-block',
    }),
    contentClass: css(buildHeroContentStyles(viewportWidth)),
  };
}

function HeroBackground({ image, css }) {
  return (
    <div
      className={css({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
      })}
    >
      <img
        src={image}
        alt="Hero"
        className={css({
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'brightness(0.7)',
        })}
      />
      <div
        className={css({
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
          zIndex: 2,
        })}
      />
    </div>
  );
}

function HeroSection({ section, theme, css, viewportWidth, mode }) {
  const shellClass = css(buildSectionShellStyles(section, theme, mode, viewportWidth));
  const textClasses = buildHeroTextClasses({ section, theme, css, viewportWidth });
  if (mode === 'preview' && typeof console !== 'undefined') {
    try {
      const height = (shellClass && typeof shellClass === 'string') ? shellClass : undefined;
      console.debug('HeroSection preview debug', { id: section.id, props: section.props, computedShell: height });
    } catch (e) {
      console.debug('HeroSection preview debug error', e);
    }
  }

  return (
    <div className={cx('hero-section', shellClass)}>
      {section.props.image ? <HeroBackground image={section.props.image} css={css} /> : null}
      <div className={cx('hero-content', textClasses.contentClass)}>
        <h2 className={textClasses.titleClass}>{section.props.title}</h2>
        <p className={textClasses.subtitleClass}>{section.props.subtitle}</p>
        {section.props.ctaText ? (
          <a href={section.props.ctaLink} className={cx('button primary', textClasses.buttonClass)}>
            {section.props.ctaText}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function buildTextHeadlineClasses({ section, theme, css, viewportWidth }) {
  return {
    titleClass: css({
      textAlign: section.props.alignment || 'center',
      fontSize: pickResponsiveValue(viewportWidth, '1.6rem', '2.5rem', '3.5rem'),
      fontFamily: theme?.fonts?.heading || FALLBACK_THEME.fonts.heading,
      color: theme?.colors?.primary || FALLBACK_THEME.colors.primary,
      marginBottom: '2rem',
      fontWeight: 300,
      letterSpacing: pickResponsiveValue(viewportWidth, '0.5px', '1px', '2px'),
      textTransform: 'uppercase',
      position: 'relative',
      paddingBottom: '1rem',
      lineHeight: pickResponsiveValue(viewportWidth, '1.2', '1.25', '1.3'),
    }),
    underlineClass: css({
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: pickResponsiveValue(viewportWidth, '50px', '65px', '80px'),
      height: '2px',
      backgroundColor: theme?.colors?.primary || FALLBACK_THEME.colors.primary,
      opacity: 0.5,
    }),
  };
}

function buildTextParagraphClass({ section, theme, css, viewportWidth }) {
  const size = section.props.size || 'medium';

  return css({
    textAlign: section.props.alignment || 'center',
    fontSize: pickResponsiveValue(
      viewportWidth,
      size === 'large' ? '1rem' : size === 'small' ? '0.8rem' : '0.9rem',
      size === 'large' ? '1.1rem' : size === 'small' ? '0.9rem' : '1rem',
      size === 'large' ? '1.2rem' : size === 'small' ? '0.9rem' : '1rem'
    ),
    lineHeight: '1.8',
    maxWidth: '800px',
    margin: '0 auto',
    color: theme?.colors?.text || FALLBACK_THEME.colors.text,
    padding: pickResponsiveValue(viewportWidth, '0 0.5rem', '0 1rem', '0'),
  });
}

function TextSection({ section, theme, css, viewportWidth }) {
  const lines = splitLines(section.props.content);
  const isHeadline = String(section.props.content || '').length < 50;

  if (isHeadline) {
    const classes = buildTextHeadlineClasses({ section, theme, css, viewportWidth });

    return (
      <div className="text-section">
        <h2 className={classes.titleClass}>
          {renderLinesWithBreaks(lines)}
          <span className={classes.underlineClass} />
        </h2>
      </div>
    );
  }

  return (
    <div className="text-section">
      <p className={buildTextParagraphClass({ section, theme, css, viewportWidth })}>
        {renderLinesWithBreaks(lines)}
      </p>
    </div>
  );
}

function buildGalleryClasses({ section, theme, css, viewportWidth }) {
  return {
    galleryClass: css({
      display: 'grid',
      gridTemplateColumns: pickResponsiveValue(
        viewportWidth,
        'repeat(1, 1fr)',
        'repeat(2, 1fr)',
        `repeat(${section.props.columns || 3}, 1fr)`
      ),
      gap: pickResponsiveValue(
        viewportWidth,
        '0.8rem',
        '1.2rem',
        section.props.spacing === 'small'
          ? '1rem'
          : section.props.spacing === 'large'
            ? '3rem'
            : '2rem'
      ),
      padding: pickResponsiveValue(viewportWidth, '1rem 0.5rem', '1.5rem 1rem', '2rem 0'),
    }),
    itemClass: css({
      position: 'relative',
      overflow: 'hidden',
      borderRadius: theme?.borderRadius?.medium || FALLBACK_THEME.borderRadius.medium,
      boxShadow: theme?.shadows?.medium || FALLBACK_THEME.shadows.medium,
      aspectRatio: '4/3',
      cursor: 'pointer',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    }),
    imageClass: css({
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    }),
    captionClass: css({
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: pickResponsiveValue(viewportWidth, '0.4rem', '0.6rem', '1rem'),
      background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
      color: '#ffffff',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    }),
    captionTextClass: css({
      margin: 0,
      fontSize: pickResponsiveValue(viewportWidth, '0.7rem', '0.8rem', '1rem'),
      fontFamily: theme?.fonts?.body || FALLBACK_THEME.fonts.body,
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    }),
  };
}

function GallerySection({ section, theme, css, viewportWidth }) {
  const classes = buildGalleryClasses({ section, theme, css, viewportWidth });
  const images = Array.isArray(section.props.images) ? section.props.images : [];

  return (
    <div className={cx('gallery-section', section.props.layout || 'grid', classes.galleryClass)}>
      {images.map((img, index) => (
        <div key={`${img}-${index}`} className={cx('gallery-item', classes.itemClass)}>
          <img src={img} alt={`Галерия ${index + 1}`} className={classes.imageClass} />
          {hasCaptionsEnabled(section.props.captions) ? (
            <div className={classes.captionClass}>
              <p className={classes.captionTextClass}>{`Снимка ${index + 1}`}</p>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function buildCountdownClasses({ section, theme, css, viewportWidth, mode }) {
  return {
    shellClass: css(buildSectionShellStyles(section, theme, mode, viewportWidth)),
    titleClass: css({
      fontSize: mode === 'preview' ? pickResponsiveValue(viewportWidth, '1.2rem', '1.35rem', '1.5rem') : undefined,
      marginBottom: mode === 'preview' ? '1rem' : undefined,
      fontFamily: theme?.fonts?.heading || FALLBACK_THEME.fonts.heading,
      color: theme?.colors?.primary || FALLBACK_THEME.colors.primary,
    }),
    dateClass: css({
      marginTop: mode === 'viewer' ? '2rem' : '1rem',
      fontSize: mode === 'preview' ? pickResponsiveValue(viewportWidth, '0.8rem', '0.85rem', '0.9rem') : undefined,
      color: theme?.colors?.text || FALLBACK_THEME.colors.text,
      padding: mode === 'preview' ? pickResponsiveValue(viewportWidth, '0 0.5rem', '0', '0') : undefined,
    }),
  };
}

function CountdownSection({ section, theme, css, viewportWidth, mode }) {
  const classes = buildCountdownClasses({ section, theme, css, viewportWidth, mode });

  return (
    <div className={cx('countdown-section', classes.shellClass)}>
      <h3 className={classes.titleClass}>{section.props.title || 'Обратно броене до нашия специален ден'}</h3>
      <CountdownDisplay
        weddingDate={section.props.weddingDate}
        expiredMessage={section.props.expiredMessage || 'Денят настъпи!'}
        theme={theme}
      />
      <p className={classes.dateClass}>{formatWeddingDate(section.props.weddingDate)}</p>
    </div>
  );
}

function buildFormFieldClasses({ fieldName, theme, css, viewportWidth, mode }) {
  return {
    label: getFieldLabel(fieldName),
    wrapperClass: css({ marginBottom: '1rem' }),
    labelClass: css({
      display: 'block',
      marginBottom: mode === 'viewer' ? '0.5rem' : '0.25rem',
      fontSize: mode === 'preview' ? pickResponsiveValue(viewportWidth, '0.8rem', '0.85rem', '0.9rem') : undefined,
      color: theme?.colors?.text || FALLBACK_THEME.colors.text,
      fontWeight: 500,
      padding: mode === 'preview' ? pickResponsiveValue(viewportWidth, '0 0.2rem', '0', '0') : undefined,
    }),
    inputBaseClass: css({
      width: '100%',
      padding: mode === 'viewer' ? '12px' : pickResponsiveValue(viewportWidth, '0.4rem', '0.5rem', '0.5rem'),
      fontSize: mode === 'preview' ? pickResponsiveValue(viewportWidth, '0.8rem', '0.85rem', '0.9rem') : '0.875rem',
      border: `1px solid ${theme?.colors?.secondary || FALLBACK_THEME.colors.secondary}`,
      borderRadius: theme?.borderRadius?.small || FALLBACK_THEME.borderRadius.small,
      fontFamily: theme?.fonts?.body || FALLBACK_THEME.fonts.body,
      boxSizing: 'border-box',
    }),
    textareaClass: css({
      minHeight: mode === 'preview' ? pickResponsiveValue(viewportWidth, '60px', '70px', '80px') : undefined,
    }),
  };
}

function FormField({ fieldName, theme, css, viewportWidth, mode }) {
  const classes = buildFormFieldClasses({ fieldName, theme, css, viewportWidth, mode });
  const placeholderPrefix = fieldName === 'message' ? 'вашето' : 'вашия';

  if (fieldName === 'message') {
    return (
      <div className={cx('form-field', classes.wrapperClass)}>
        <label className={classes.labelClass}>{classes.label}</label>
        <textarea
          placeholder={`Въведете ${placeholderPrefix} ${classes.label.toLowerCase()}`}
          className={cx(classes.inputBaseClass, classes.textareaClass)}
        />
      </div>
    );
  }

  return (
    <div className={cx('form-field', classes.wrapperClass)}>
      <label className={classes.labelClass}>{classes.label}</label>
      <input
        type={fieldName === 'email' ? 'email' : fieldName === 'phone' ? 'tel' : 'text'}
        placeholder={`Въведете ${placeholderPrefix} ${classes.label.toLowerCase()}`}
        className={classes.inputBaseClass}
      />
    </div>
  );
}

function getFormFields(section) {
  return String(section.props.fields || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

function translateSubmitText(value) {
  if (value === 'Submit' || value === 'Send') return 'Изпрати';
  if (value === 'Contact Us') return 'Свържете се с нас';
  return value || 'Изпрати';
}

function FormSection({ section, theme, css, viewportWidth, mode }) {
  const shellClass = css(buildSectionShellStyles(section, theme, mode, viewportWidth));
  const buttonClass = css({
    backgroundColor: DESIGN_SYSTEM_BUTTON_COLOR,
    color: '#ffffff',
    padding: mode === 'viewer' ? undefined : pickResponsiveValue(viewportWidth, '0.4rem 0.8rem', '0.5rem 1rem', '0.5rem 1rem'),
    fontSize: mode === 'viewer' ? undefined : pickResponsiveValue(viewportWidth, '0.8rem', '0.85rem', '0.9rem'),
    border: 'none',
    borderRadius: theme?.borderRadius?.small || FALLBACK_THEME.borderRadius.small,
    cursor: 'pointer',
    fontFamily: theme?.fonts?.body || FALLBACK_THEME.fonts.body,
    fontWeight: 500,
    transition: mode === 'viewer' ? 'background-color 0.2s ease' : undefined,
  });
  const fields = getFormFields(section);

  return (
    <div className={cx('form-section', shellClass)}>
      {fields.map((fieldName) => (
        <FormField
          key={fieldName}
          fieldName={fieldName}
          theme={theme}
          css={css}
          viewportWidth={viewportWidth}
          mode={mode}
        />
      ))}
      <button className={cx('button primary', buttonClass)}>{translateSubmitText(section.props.submitText)}</button>
    </div>
  );
}

function buildEmailFormWrapperClass({ section, css, viewportWidth, mode }) {
  return css({
    maxWidth:
      mode === 'preview'
        ? pickResponsiveValue(
            viewportWidth,
            '100%',
            section.props?.width === 'narrow' ? '500px' : section.props?.width === 'wide' ? '700px' : '600px',
            section.props?.width === 'narrow' ? '600px' : section.props?.width === 'wide' ? '1000px' : '800px'
          )
        : section.props?.width === 'narrow'
          ? '600px'
          : section.props?.width === 'wide'
            ? '1000px'
            : '800px',
    margin: '0 auto',
  });
}

function EmailFormSection({ section, theme, css, viewportWidth, mode }) {
  const outerClass = css(buildSectionShellStyles(section, theme, mode, viewportWidth));
  const widthClass = buildEmailFormWrapperClass({ section, css, viewportWidth, mode });

  return (
    <div className={outerClass}>
      <div className={widthClass}>
        <EmailForm
          section={{
            ...section,
            props: {
              ...section.props,
              primaryColor: section.props?.buttonColor || FALLBACK_THEME.colors.primary,
              textColor: section.props?.textColor || theme?.colors?.text || FALLBACK_THEME.colors.text,
              backgroundColor: section.props?.backgroundColor || theme?.colors?.background || FALLBACK_THEME.colors.background,
            },
          }}
        />
      </div>
    </div>
  );
}

function WeddingPhotoGallerySection({ section, theme, mode, onWeddingGalleryPhotoUpload }) {
  return (
    <WeddingPhotoGallery
      {...section.props}
      theme={theme}
      allowUpload={mode === 'viewer' ? true : section.props.allowUpload}
      sectionId={section.id}
      onPhotoUpload={(imageUrl) => {
        if (typeof onWeddingGalleryPhotoUpload === 'function') {
          onWeddingGalleryPhotoUpload(section, imageUrl);
        }
      }}
    />
  );
}

function BestMenAndMaidsSection({ section, theme, onBestMenAndMaidsPhotoUpload }) {
  return (
    <BestMenAndMaids
      section={section}
      theme={theme}
      onPhotoUpload={(fileOrUrl) => {
        if (typeof onBestMenAndMaidsPhotoUpload === 'function') {
          onBestMenAndMaidsPhotoUpload(section, fileOrUrl);
        }
      }}
    />
  );
}

export function renderBasicSectionContent({
  section,
  theme,
  css,
  viewportWidth,
  mode,
  onWeddingGalleryPhotoUpload,
  onBestMenAndMaidsPhotoUpload,
}) {
  switch (section.type) {
    case 'hero':
      return <HeroSection section={section} theme={theme} css={css} viewportWidth={viewportWidth} mode={mode} />;
    case 'text':
      return <TextSection section={section} theme={theme} css={css} viewportWidth={viewportWidth} />;
    case 'gallery':
      return <GallerySection section={section} theme={theme} css={css} viewportWidth={viewportWidth} />;
    case 'countdown':
      return (
        <CountdownSection
          section={section}
          theme={theme}
          css={css}
          viewportWidth={viewportWidth}
          mode={mode}
        />
      );
    case 'form':
      return (
        <FormSection
          section={section}
          theme={theme}
          css={css}
          viewportWidth={viewportWidth}
          mode={mode}
        />
      );
    case 'emailForm':
      return (
        <EmailFormSection
          section={section}
          theme={theme}
          css={css}
          viewportWidth={viewportWidth}
          mode={mode}
        />
      );
    case 'weddingPhotoGallery':
      return (
        <WeddingPhotoGallerySection
          section={section}
          theme={theme}
          mode={mode}
          onWeddingGalleryPhotoUpload={onWeddingGalleryPhotoUpload}
        />
      );
    case 'bestMenAndMaids':
      return (
        <BestMenAndMaidsSection
          section={section}
          theme={theme}
          onBestMenAndMaidsPhotoUpload={onBestMenAndMaidsPhotoUpload}
        />
      );
    default:
      return null;
  }
}
